import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Box,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  Save as SaveIcon,
  VideoLibrary as VideoIcon,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import ValidatedTextField from '../../Components/ValidatedTextField';
import ImageSelectorButton from '../../Components/ImageSelectorButton';
import Question from './Question';
import QuestionList from './QuestionList';
import { useApiSend } from '../../utils/httpClientTs';
import { submitTrainingAPI } from '../../endpoints/training';
import { nameValidator } from '../../utils/validators';
import AlertItem from '../../Components/AlertItem';
import TrainingCategorySelector from '../../Components/TrainingCategorySelector';
import TrainingTopicSelector from '../../Components/TrainingTopicSelector';
import TrainingGroupSelector from '../../Components/TrainingGroupSelector';
import TrainingRolesSelector from '../../Components/TrainingRolesSelector';
import {
  TrainingCategory,
  TrainingTopic,
  TrainingGroup,
  TrainingRole,
  TrainingQuestion,
  TrainingCreateRequest,
} from '../../types/training';

const steps = [
  'Basic Information',
  'Content & Media',
  'Quiz Questions',
  'Taxonomy & Settings',
];

const TrainingCreate: React.FC = () => {
  const navigate = useNavigate();

  // Form state
  const [trainingName, setTrainingName] = useState('');
  const [nameIsValid, setNameIsValid] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [trainingDescription, setTrainingDescription] = useState('');
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const [trainingImage, setTrainingImage] = useState<File | null>(null);
  const [videoLink, setVideoLink] = useState('');
  const [videoLinkError, setVideoLinkError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState('');
  const [transcriptError, setTranscriptError] = useState<string | null>(null);
  const [summary, setSummary] = useState('');
  const [summaryError, setSummaryError] = useState<string | null>(null);

  // Quiz state
  const [addQuestion, setAddQuestion] = useState(false);
  const [questions, setQuestions] = useState<TrainingQuestion[]>([]);

  // Taxonomy state
  const [categories, setCategories] = useState<TrainingCategory[]>([]);
  const [topics, setTopics] = useState<TrainingTopic[]>([]);
  const [group, setGroup] = useState<TrainingGroup | null>(null);
  const [createGroup, setCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [createTopic, setCreateTopic] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');
  const [createCategory, setCreateCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [roles, setRoles] = useState<TrainingRole[]>([]);

  // Stepper state
  const [activeStep, setActiveStep] = useState(0);

  // Validation error state
  const [validationError, setValidationError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const { mutate, isPending } = useApiSend(
    submitTrainingAPI,
    (data) => {
      resetState();
      navigate('/training');
    },
    (error: any) => {
      console.error('Error creating training:', error);
      const errorMessage =
        error?.message ||
        error?.error ||
        'Error creating training. Please try again later.';
      setApiError(errorMessage);
    }
  );

  const resetState = () => {
    setTrainingName('');
    setNameIsValid(false);
    setNameError(false);
    setValidationError(null);
    setApiError(null);
    setTrainingDescription('');
    setDescriptionError(null);
    setTrainingImage(null);
    setVideoLink('');
    setVideoLinkError(null);
    setTranscript('');
    setTranscriptError(null);
    setSummary('');
    setSummaryError(null);
    setAddQuestion(false);
    setQuestions([]);
    setCategories([]);
    setTopics([]);
    setGroup(null);
    setCreateGroup(false);
    setNewGroupName('');
    setCreateTopic(false);
    setNewTopicName('');
    setCreateCategory(false);
    setNewCategoryName('');
    setRoles([]);
    setActiveStep(0);
  };

  const handleNameChange = (isValid: boolean, value: string) => {
    setNameIsValid(!isValid);
    setTrainingName(value);
    setNameError(value.length > 255);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTrainingDescription(value);
    if (value.length > 255) {
      setDescriptionError('Description must be less than 255 characters');
    } else if (value.length === 0) {
      setDescriptionError('Description is required');
    } else {
      setDescriptionError(null);
    }
  };

  const handleQuestionUpdate = (updatedQuestions: TrainingQuestion[]) => {
    const questionsWithOrder = updatedQuestions.map((question, index) => ({
      ...question,
      order: index + 1,
    }));
    setQuestions(questionsWithOrder);
  };

  const handleAddQuestion = (question: TrainingQuestion) => {
    const totalQuestions = questions.length;
    const questionWithOrder = { ...question, order: totalQuestions + 1 };
    setQuestions([...questions, questionWithOrder]);
    setAddQuestion(false);
  };

  const handleVideoLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setVideoLink(value);
    setVideoLinkError(value.length === 0 ? 'Video Link is required' : null);
  };

  const handleTranscriptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTranscript(value);
    setTranscriptError(value.length === 0 ? 'Transcript is required' : null);
  };

  const handleSummaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSummary(value);
    setSummaryError(value.length === 0 ? 'Summary is required' : null);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setTrainingImage(file);
    }
  };

  const handleCancelNew = (type: 'group' | 'topic' | 'category') => {
    switch (type) {
      case 'group':
        setCreateGroup(false);
        setNewGroupName('');
        break;
      case 'topic':
        setCreateTopic(false);
        setNewTopicName('');
        break;
      case 'category':
        setCreateCategory(false);
        setNewCategoryName('');
        break;
    }
  };

  const buildRequest = (): TrainingCreateRequest => {
    const ret: TrainingCreateRequest = {
      name: trainingName,
      description: trainingDescription,
      video_url: videoLink,
      summary: summary,
      transcript: transcript,
    };

    // Categories - now using string array
    const tempCategories: string[] = [];
    categories.forEach((category) => {
      tempCategories.push(category.id);
    });
    if (createCategory && newCategoryName) {
      tempCategories.push(newCategoryName);
    }
    if (tempCategories.length > 0) {
      ret.categories = tempCategories;
    }

    // Topics - now using string array
    const tempTopics: string[] = [];
    topics.forEach((topic) => {
      tempTopics.push(topic.id);
    });
    if (createTopic && newTopicName) {
      tempTopics.push(newTopicName);
    }
    if (tempTopics.length > 0) {
      ret.topics = tempTopics;
    }

    // Group - keeping as object for now
    if (group) {
      ret.group = { id: group.id, step: 1 };
    } else if (createGroup && newGroupName) {
      ret.group = { name: newGroupName, step: 1 };
    }

    // Roles - now using string array
    const tempRoles = roles.map((role) => role.id);
    if (tempRoles.length > 0) {
      ret.roles = tempRoles;
    }

    // Questions
    if (questions.length > 0) {
      ret.questions = questions;
    }

    return ret;
  };

  const validateForm = (): boolean => {
    let valid = true;

    if (trainingName.length === 0) {
      setNameError(true);
      valid = false;
    }
    if (trainingDescription.length === 0) {
      setDescriptionError('Description is required');
      valid = false;
    }
    if (videoLink.length === 0) {
      setVideoLinkError('Video Link is required');
      valid = false;
    }
    if (transcript.length === 0) {
      setTranscriptError('Transcript is required');
      valid = false;
    }
    if (summary.length === 0) {
      setSummaryError('Summary is required');
      valid = false;
    }
    if (
      nameIsValid ||
      nameError ||
      descriptionError ||
      videoLinkError ||
      transcriptError ||
      summaryError
    ) {
      valid = false;
    }

    return valid;
  };

  const submitTraining = (e: React.FormEvent) => {
    e.preventDefault();
    // Clear previous errors
    setValidationError(null);
    setApiError(null);

    if (!validateForm()) {
      // Collect validation error messages
      const errors: string[] = [];
      if (trainingName.length === 0 || nameIsValid || nameError) {
        errors.push('Training name is required and must be valid');
      }
      if (trainingDescription.length === 0 || descriptionError) {
        errors.push('Description is required');
      }
      if (videoLink.length === 0 || videoLinkError) {
        errors.push('Video link is required');
      }
      if (transcript.length === 0 || transcriptError) {
        errors.push('Transcript is required');
      }
      if (summary.length === 0 || summaryError) {
        errors.push('Summary is required');
      }

      const errorMessage =
        errors.length > 0
          ? `Please fix the following errors: ${errors.join(', ')}`
          : 'Please complete all required fields before submitting.';
      setValidationError(errorMessage);
      return;
    }

    const request = buildRequest();
    mutate(request);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary">
                Basic Information
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <ValidatedTextField
                label="Training Name"
                value={trainingName}
                validator={nameValidator}
                valueRequired={true}
                errorOverride={
                  nameError ? 'Name must be less than 255 characters' : null
                }
                onChange={handleNameChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                id="description"
                label="Description"
                size="small"
                fullWidth
                multiline
                helperText={descriptionError}
                error={!!descriptionError}
                rows={4}
                value={trainingDescription}
                onChange={handleDescriptionChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="summary"
                label="Summary"
                size="small"
                fullWidth
                multiline
                helperText={summaryError}
                error={!!summaryError}
                rows={4}
                value={summary}
                onChange={handleSummaryChange}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary">
                Content & Media
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 2, textAlign: 'center' }}>
                {trainingImage ? (
                  <>
                    <Typography variant="h6" gutterBottom color="primary">
                      Logo Preview
                    </Typography>
                    <Box
                      component="img"
                      sx={{
                        height: 100,
                        width: 'auto',
                        maxWidth: 100,
                        borderRadius: '10px',
                        mb: 2,
                      }}
                      alt="Training logo"
                      src={URL.createObjectURL(trainingImage)}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => setTrainingImage(null)}
                    >
                      Clear
                    </Button>
                  </>
                ) : (
                  <ImageSelectorButton
                    label="Training Image"
                    handleFileUpload={handleFileUpload}
                  />
                )}
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Upload an image to be used as the training logo
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                id="video-link"
                label="Video Link"
                size="small"
                fullWidth
                helperText={videoLinkError}
                error={!!videoLinkError}
                value={videoLink}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VideoIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={handleVideoLinkChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="transcript"
                label="Video Transcript"
                size="small"
                fullWidth
                multiline
                helperText={transcriptError}
                error={!!transcriptError}
                rows={4}
                value={transcript}
                onChange={handleTranscriptChange}
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary">
                Quiz Questions
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Add quiz questions to test user knowledge (optional)
              </Typography>
            </Grid>
            <Grid item xs={12} textAlign="center">
              {!addQuestion && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setAddQuestion(true)}
                >
                  Add Question
                </Button>
              )}
              {addQuestion && (
                <Question
                  onAddQuestion={handleAddQuestion}
                  onCancel={() => setAddQuestion(false)}
                />
              )}
            </Grid>
            {questions.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Current Questions ({questions.length})
                </Typography>
                <QuestionList
                  questions={questions}
                  onRemoveQuestion={(index) =>
                    setQuestions(questions.filter((_, i) => i !== index))
                  }
                  onQuestionOrderUpdate={handleQuestionUpdate}
                />
              </Grid>
            )}
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary">
                Taxonomy & Settings
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TrainingCategorySelector
                value={categories}
                handleValueChange={(value) => setCategories(value)}
                label="Training Category"
              />
              {createCategory ? (
                <TextField
                  id="new-category"
                  label="New Category"
                  size="small"
                  fullWidth
                  value={newCategoryName}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        onClick={() => handleCancelNew('category')}
                        position="end"
                      >
                        <CancelIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  sx={{ mt: 2 }}
                />
              ) : (
                <Button
                  size="small"
                  onClick={() => setCreateCategory(true)}
                  startIcon={<AddIcon />}
                  sx={{ mt: 2 }}
                >
                  Create New Category
                </Button>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <TrainingTopicSelector
                value={topics}
                handleValueChange={(value) => setTopics(value)}
                label="Training Topic"
              />
              {createTopic ? (
                <TextField
                  id="new-topic"
                  label="New Topic"
                  size="small"
                  fullWidth
                  value={newTopicName}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        onClick={() => handleCancelNew('topic')}
                        position="end"
                      >
                        <CancelIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => setNewTopicName(e.target.value)}
                  sx={{ mt: 2 }}
                />
              ) : (
                <Button
                  size="small"
                  onClick={() => setCreateTopic(true)}
                  startIcon={<AddIcon />}
                  sx={{ mt: 2 }}
                >
                  Create New Topic
                </Button>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <TrainingGroupSelector
                value={group}
                handleValueUpdate={(value) => setGroup(value)}
                label="Training Series"
              />
              {createGroup ? (
                <TextField
                  id="new-series"
                  label="New Series"
                  size="small"
                  fullWidth
                  value={newGroupName}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        onClick={() => handleCancelNew('group')}
                        position="end"
                      >
                        <CancelIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  sx={{ mt: 2 }}
                />
              ) : (
                <Button
                  size="small"
                  onClick={() => setCreateGroup(true)}
                  startIcon={<AddIcon />}
                  sx={{ mt: 2 }}
                >
                  Create New Series
                </Button>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <TrainingRolesSelector
                value={roles}
                handleValueChange={(value) => setRoles(value)}
                label="Training Roles"
              />
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography
            variant="h4"
            component="h1"
            textAlign="center"
            color="primary"
            gutterBottom
          >
            Create Training
          </Typography>
        </Grid>

        {/* Validation Error Alert */}
        {validationError && (
          <Grid item xs={12}>
            <AlertItem
              severity="error"
              description={validationError}
              onClose={() => setValidationError(null)}
              keepOpen={true}
            />
          </Grid>
        )}

        {/* API Error Alert */}
        {apiError && (
          <Grid item xs={12}>
            <AlertItem
              severity="error"
              description={apiError}
              onClose={() => setApiError(null)}
              keepOpen={true}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>{renderStepContent(activeStep)}</CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
              Back
            </Button>
            <Box>
              {activeStep === steps.length - 1 ? (
                <LoadingButton
                  loading={isPending}
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={submitTraining}
                >
                  Create Training
                </LoadingButton>
              ) : (
                <Button variant="contained" onClick={handleNext}>
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TrainingCreate;
